import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

interface ChatContextValue {
  isOpen: boolean;
  pendingQuestion: string | null;
  openChat: () => void;
  closeChat: () => void;
  openWithQuestion: (question: string) => void;
  clearPendingQuestion: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openWithQuestion = useCallback((question: string) => {
    setPendingQuestion(question);
    setIsOpen(true);
  }, []);

  const clearPendingQuestion = useCallback(() => {
    setPendingQuestion(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        pendingQuestion,
        openChat,
        closeChat,
        openWithQuestion,
        clearPendingQuestion,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
